import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Hvc } from '../components';
import { RefreshComponent } from '../Refresh';
import { PlusSVG } from '../svgs';
import TextPrompt from '../text-prompt';
import { InputSearchOptions } from './options';
import { buildSearchModel, getDisplayParts } from './search-helpers';
import { SelectedItems } from './selected-items';
import type { IInputSearchNew } from './utils';

// eslint-disable-next-line react/display-name
export const TypeInputSearchNew = forwardRef(
	(
		{
			label = '',
			error = '',
			value = '',
			options,
			minComponent = false,
			multiSelect = false,
			closeOnSelect = false,
			selected = [],
			setSelected,
			fetchResults,
			load,
			onRemoveValue,
			stopTypeSearch,
			onAddNew,
			onClear,
			compact,
			...props
		}: IInputSearchNew,
		ref
	) => {
		const isCompact = compact ?? (multiSelect && stopTypeSearch && (options?.length ?? 0) <= 12);
		const debounceTimeout = useRef<number | null>(null);

		const [inputValue, setInputValue] = useState<string>('');
		const [showOptions, setShowOptions] = useState<boolean>(false);
		const [highlightedIndex, setHighlightedIndex] = useState(0);

		const wrapperRef = useRef<HTMLDivElement>(null);

		const handleClickOutside = useCallback((event: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setShowOptions(false);
			}
		}, []);

		useEffect(() => {
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}, [handleClickOutside]);

		const onRemoveItems = useCallback(
			(value: string | number) => {
				if (!value) return;
				setSelected?.([...(selected || []).filter((i) => i.value !== value)]);
			},
			[selected]
		);
		const onRemoveAll = () => {
			setSelected?.([]);
		};

		const searchTerm = inputValue.trim();
		const selectableOptions = useMemo(() => {
			const selectedValues = new Set((selected ?? []).map((item) => item.value));
			return (options ?? []).filter((item) => (multiSelect ? !selectedValues.has(item.value) : true));
		}, [options, selected, multiSelect]);

		const recentKey = `input-search-recent:${props.name || props.id || label || 'default'}`;
		const recentResults = useMemo(() => {
			try {
				const stored = JSON.parse(localStorage.getItem(recentKey) || '[]') as { label: string; value: string | number }[];
				if (!Array.isArray(stored)) return [];
				const optionMap = new Map((options ?? []).map((item) => [String(item.value), item]));
				return stored.map((item) => optionMap.get(String(item.value)) ?? item).slice(0, 5);
			} catch {
				return [];
			}
		}, [recentKey, options]);

		const popularResults = useMemo(() => {
			const unique = new Map<string, { label: string; value: string | number }>();
			(options ?? [])
				.slice()
				.sort((a, b) => a.label.localeCompare(b.label))
				.forEach((item) => {
					const key = String(item.value);
					if (!unique.has(key)) unique.set(key, item);
				});
			return Array.from(unique.values()).slice(0, 5);
		}, [options]);

		const model = useMemo(
			() =>
				buildSearchModel({
					options: selectableOptions,
					searchTerm,
					recentResults,
					popularResults,
					showSections: !searchTerm && !isCompact,
					groupBy: !isCompact,
				}),
			[selectableOptions, searchTerm, recentResults, popularResults, isCompact]
		);

		useEffect(() => {
			if (!showOptions) {
				setHighlightedIndex(0);
				return;
			}
			setHighlightedIndex(0);
		}, [showOptions, searchTerm, model.entries.length]);

		const handleDebouncedSearch = (value: string) => {
			if (!value) return;
			// Clear the previous timeout
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}

			// Set a new timeout
			debounceTimeout.current = window.setTimeout(() => {
				fetchResults?.(value);
			}, 500);
		};

		const onRefresh = () => {
			fetchResults?.('');
		};

		const handleClear = () => {
			setInputValue('');
			onRemoveValue?.();
			onClear?.();
			if (!stopTypeSearch) {
				fetchResults?.('');
			}
		};

		const onSelectAll = () => {
			setSelected?.(options || []);
		};

		const saveRecent = (item: { label: string; value: string | number }) => {
			try {
				const stored = JSON.parse(localStorage.getItem(recentKey) || '[]') as { label: string; value: string | number }[];
				const next = [item, ...(stored || []).filter((i) => String(i.value) !== String(item.value))].slice(0, 5);
				localStorage.setItem(recentKey, JSON.stringify(next));
			} catch {
				localStorage.setItem(recentKey, JSON.stringify([item]));
			}
		};

		const handleSelect = (item: { label: string; value: string | number }) => {
			if (multiSelect) {
				const exists = (selected ?? []).some((entry) => String(entry.value) === String(item.value));
				const items = exists
					? (selected ?? []).filter((entry) => String(entry.value) !== String(item.value))
					: [...(selected ?? []), item];
				setSelected?.(items);
				setInputValue('');
				setShowOptions(!closeOnSelect);
				return;
			}

			setSelected?.([item]);
			setInputValue('');
			setShowOptions(false);
			saveRecent(item);
		};

		const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
			if (!showOptions) return;
			// Escape closes even an empty list, and only the list — stopPropagation keeps the
			// document-level listener of a host right panel or modal from closing that too.
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				setShowOptions(false);
				return;
			}
			if (!model.selectableEntries.length) return;
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				setHighlightedIndex((prev) => Math.min(prev + 1, model.selectableEntries.length - 1));
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				setHighlightedIndex((prev) => Math.max(prev - 1, 0));
			} else if (event.key === 'Enter') {
				event.preventDefault();
				const target = model.selectableEntries[highlightedIndex]?.item;
				if (target) handleSelect(target);
			}
		};

		return (
			<TypeInputClass className={props.className || ''}>
				<div
					className={`form-container f-column-9 position-relative ${props.className || ''} ${isCompact ? 'compact' : ''}`}
					data-input-search={isCompact ? 'compact' : undefined}
				>
					<Hvc
						removeDOM
						view={!!label}
						className={`f-row align-items-center justify-content-between ${onAddNew || multiSelect ? 'w-100' : 'hw-mx'}`}
					>
						<div className='f-row-20 align-items-center'>
							<label
								className='m-0'
								htmlFor={props.id || props.name}
							>
								{label}
							</label>
							<Hvc
								removeDOM
								view
							>
								<RefreshComponent
									load={load}
									onRefresh={onRefresh}
								/>
							</Hvc>
						</div>
						<div className='f-row-20 align-items-center justify-content-end'>
							<Hvc
								removeDOM
								view={!!onAddNew}
								className='hw-mx cursor-pointer f-row-5 align-items-center'
								onClick={onAddNew}
							>
								<p className='m-0 font-12 color-label'>Add</p>
								<PlusSVG color='#8a8a8a' />
							</Hvc>
							<Hvc
								removeDOM
								view={!!multiSelect}
								className='hw-mx cursor-pointer'
								onClick={onSelectAll}
							>
								<p className='m-0 font-12 color-label'>Select All</p>
							</Hvc>
						</div>
					</Hvc>
					<div
						className='position-relative w-100'
						ref={wrapperRef}
					>
						<input
							{...props}
							ref={ref as React.LegacyRef<HTMLInputElement> | undefined}
							className={`${error ? 'is-error' : ''} ${minComponent ? 'min-size' : ''}`}
							disabled={props.disabled}
							// style={minStyle(minComponent)}
							onFocus={() => setShowOptions(true)}
							onKeyDown={handleKeyDown}
							value={inputValue || ''}
							onChange={(e) => {
								setInputValue(e.target.value);
								if (!stopTypeSearch) handleDebouncedSearch(e.target.value);
							}}
							placeholder={
								props.placeholder || (label.toLowerCase().includes('party') ? 'Type to search parties...' : 'Type to search...')
							}
						/>
						{/* when theres a value in the input field the close component is visible and when clicked the value is removed */}
						<Hvc
							removeDOM
							view={!!inputValue}
							className='position-absolute cursor-pointer px-2 py-1 rounded bg-input'
							style={{
								right: '4px',
								top: minComponent && !label ? '9px' : '3px',
							}}
							onClick={handleClear}
						>
							<i className='fas fa-times color-danger text-little' />
						</Hvc>
						{!multiSelect && selected?.length ? (
							<div className='search-selected-single'>
								<div className='search-chip'>
									<span className='search-chip-primary'>{getDisplayParts(selected[0].label).primary}</span>
									{getDisplayParts(selected[0].label).secondary ? (
										<span className='search-chip-secondary'>{getDisplayParts(selected[0].label).secondary}</span>
									) : null}
									<button
										type='button'
										onMouseDown={() => {
											setSelected?.([]);
											setInputValue('');
										}}
									>
										<i className='fas fa-times' />
									</button>
								</div>
							</div>
						) : null}
						<Hvc
							removeDOM
							view={!label && !!fetchResults}
							className='position-absolute cursor-pointer px-2 py-1 rounded bg-input'
							style={{
								right: inputValue ? '21px' : '4px',
								top: minComponent ? '9px' : '9px',
							}}
						>
							<RefreshComponent
								load={load}
								onRefresh={onRefresh}
							/>
						</Hvc>
						{/* if multi select is enabled, the items selected will be visible below the input field */}
						<Hvc
							view={!!multiSelect && !!selected?.length}
							className='pt-2 search-selected'
						>
							<SelectedItems
								items={selected ?? []}
								onRemove={onRemoveItems}
								onRemoveAll={onRemoveAll}
							/>
						</Hvc>
					</div>
					{/* show options to select from */}
					<InputSearchOptions
						showOptions={showOptions}
						searchTerm={searchTerm}
						model={model}
						highlightedIndex={highlightedIndex}
						setHighlightedIndex={setHighlightedIndex}
						onSelect={handleSelect}
						compact={isCompact}
					/>
					{!!error && (
						<TextPrompt
							prompt={error}
							status={false}
						/>
					)}
				</div>
			</TypeInputClass>
		);
	}
);

const TypeInputClass = styled.div`
	position: relative;
	input {
		width: 100%;
		height: 3.125rem;
		background: none;
		border: 1px solid #a1a1a1;
		border-radius: 0.1875rem;
		padding: 0 1.24rem 0 0.7rem;
		box-shadow: none;
		box-sizing: border-box;
		outline: none;
		font-size: 0.8425rem;
		&::placeholder {
			color: var(--input-placeholder, #a1a1a1);
			font-size: 0.8125rem;
			font-style: normal;
			font-weight: 300;
			line-height: normal;
		}
		&:focus {
			&.is-error {
				border: 1px solid #f56e9d;
			}
		}
		&.is-error {
			border: 1px solid #f56e9d;
			margin-bottom: 0.81rem;
		}
		&.isonlyview {
			padding-left: 0;
			border: none;
		}

		&.min-size {
			font-size: 12px;
			height: 40px;
			border: 1px solid #d1d1d1;

			@media (max-width: 768px) {
				font-size: 16px;
			}
		}

		@media (max-width: 768px) {
			font-size: 16px;
		}
	}
	.search-container {
		position: absolute;
		top: calc(100% + 8px);
		left: 0;
		right: 0;
		border: 1px solid var(--border-secondary, #eeeeee);
		background-color: var(--card-bg, #fff);
		margin: 0;
		padding: 10px;
		border-radius: 14px;
		box-shadow: 0 16px 30px rgba(15, 26, 46, 0.12);
		z-index: 1000;
	}

	.search-container.compact {
		padding: 8px;
		border-radius: 12px;
	}

	.search-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 6px 8px 10px;
		border-bottom: 1px solid var(--border-secondary, #eeeeee);
		gap: 8px;
	}

	.search-count {
		font-size: 12px;
		font-weight: 600;
		color: var(--text-primary, #212121);
	}

	.search-hint {
		font-size: 11px;
		color: var(--text-tertiary, #757575);
		text-align: right;
	}

	.search-list {
		margin-top: 8px;
		overflow-y: auto;
		position: relative;
		padding-right: 18px;
	}

	.form-container.compact .search-list {
		margin-top: 0;
		padding-right: 0;
	}

	.search-section,
	.search-group {
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--text-tertiary, #757575);
		padding: 6px 8px;
		background: var(--card-bg, #ffffff);
		position: sticky;
		top: 0;
		z-index: 2;
	}

	.search-group {
		background: var(--bg-secondary, #fafafa);
		border-radius: 8px;
	}

	.search-item {
		display: grid;
		grid-template-columns: 56px 1fr;
		gap: 10px;
		padding: 10px 12px;
		border-radius: 10px;
		cursor: pointer;
		align-items: center;
	}

	.search-item:hover,
	.search-item.is-active {
		background: var(--bg-hover, #f5f5f5);
	}

	.search-item-code {
		font-size: 13px;
		font-weight: 700;
		color: var(--text-link, #1b6b4a);
		background: var(--bg-accent, #e6f7fa);
		padding: 4px 6px;
		border-radius: 8px;
		text-align: center;
	}

	.search-item-meta {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.search-item.no-code {
		grid-template-columns: 1fr;
	}

	.search-item-name {
		font-size: 13px;
		font-weight: 600;
		color: var(--text-primary, #212121);
	}

	.search-empty {
		padding: 12px;
		font-size: 12px;
		color: var(--text-tertiary, #757575);
		text-align: center;
	}

	.search-alpha {
		position: absolute;
		top: 52px;
		right: 8px;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.search-alpha button {
		border: none;
		background: transparent;
		padding: 2px 4px;
		font-size: 10px;
		color: var(--text-tertiary, #757575);
		cursor: pointer;
	}

	.search-alpha button:hover {
		color: var(--text-link, #1b6b4a);
	}

	.search-selected-single {
		margin-top: 8px;
	}

	.search-chip {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 6px 10px;
		border-radius: 999px;
		background: var(--bg-secondary, #fafafa);
		border: 1px solid var(--border-secondary, #eeeeee);
		font-size: 12px;
	}

	.search-chip-primary {
		font-weight: 700;
		color: var(--text-primary, #212121);
	}

	.search-chip-secondary {
		color: var(--text-secondary, #616161);
	}

	.search-chip button {
		border: none;
		background: transparent;
		color: var(--text-tertiary, #757575);
		cursor: pointer;
		padding: 0;
	}
`;
